import { ArrowUpRight, ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import type { ProjectSummary } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { getEffectiveStatus } from "@/lib/project-status";
import { cn } from "@/lib/utils";

interface ProjectCardProps {
  project: ProjectSummary;
  onClick?: () => void;
}

export function ProjectCard({ project, onClick }: ProjectCardProps) {
  const status = getEffectiveStatus(project);

  return (
    <Card
      className={cn(
        "flex flex-col",
        onClick &&
          "focus-visible:ring-ring cursor-pointer transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:outline-none",
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{project.project_name}</CardTitle>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-muted-foreground mb-3 line-clamp-2 text-sm break-words">
          {project.description}
        </p>
        <div className="text-muted-foreground flex items-center justify-between text-xs">
          <span>
            {project.owner_name || project.owner_email?.split("@")[0] || "Unknown"} ·{" "}
            {formatDate(project.created_at)}
          </span>
          {project.github_url && (
            <a
              href={project.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              GitHub <ExternalLink className="size-3" />
            </a>
          )}
        </div>
        {project.error_message && (
          <p className="text-destructive mt-2 text-xs">{project.error_message}</p>
        )}
      </CardContent>
      {project.app_url && (
        <CardFooter className="mt-auto shrink-0 border-t px-6 py-2.5">
          <a
            href={project.app_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Open Application <ArrowUpRight className="size-3.5" />
          </a>
        </CardFooter>
      )}
    </Card>
  );
}
